import { useEffect, useRef, useState } from 'react'
import ConfirmModal from '../components/confirmModal'
import ExamWatermark from '../components/examWatermarks'
import { EXAM_STAGES } from '../constants/config'
import { EXAM_GUARDS } from '../constants/examGuards'
import { STRINGS } from '../constants/strings'
import { useAuth, useExam, useExamGuards } from '../hooks'

function fmt(s) {
  const sec = Math.max(0, Math.floor(s))
  const m = Math.floor(sec / 60), ss = sec % 60
  return `${m}:${String(ss).padStart(2, '0')}`
}

function QuestionItem({ q, idx, active, onClick }) {
  const { state, dispatch } = useExam()
  const answer = state.answers[idx] ?? (q.type === 'msq' ? [] : '')
  const set = v => dispatch({ type: 'SET_ANSWER', payload: { idx, answer: v } })
  const toggleMsq = key => {
    const cur  = Array.isArray(answer) ? answer : []
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]
    set(next)
  }
  const answered = Array.isArray(answer) ? answer.length > 0 : answer.trim().length > 0

  return (
    <div id={`q-${idx}`} className={`card p-5 sm:p-7 transition-all ${active ? 'border-brand/40 bg-brand/5' : ''}`}>
      <div className="flex items-start justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
        <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-md ${active ? 'bg-brand/20 text-brand' : 'bg-[var(--surface-2)] text-[var(--text-3)]'}`}>
          Q{idx + 1}
        </span>
        {q.topic && <span className="text-[0.65rem] text-[var(--text-3)]">{q.topic}</span>}
        {state.selectedExam?.showQuestionTypeIndicator && (q.type === 'mcq' || q.type === 'msq') && (
          <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-md bg-[var(--surface-2)] text-brand">
            {q.type === 'msq' ? 'Multiple options are selectable' : 'Only one option is selectable'}
          </span>
        )}
        <span className="ml-auto text-[0.65rem] font-semibold px-2 py-0.5 rounded-md bg-[var(--surface-2)] text-[var(--text-3)]">
          {q.marks ?? 1} {q.marks === 1 ? 'mark' : 'marks'}
        </span>
        {answered && <span className="text-[0.65rem] text-emerald-400">✓ answered</span>}
      </div>

      <p className="text-sm text-[var(--text-1)] font-medium leading-relaxed mb-3 sm:mb-4" onClick={onClick}>
        {q.question}
      </p>

      {q.type === 'mcq' && (
        <div className="space-y-2">
          {Object.entries(q.options).map(([k, v]) => (
            <button
              key={k}
              onClick={() => set(k)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-xs text-left transition-all ${
                answer === k
                  ? 'bg-brand/10 border-brand/40 text-brand'
                  : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)]'
              }`}
            >
              <span className={`w-5 h-5 flex-shrink-0 rounded-md flex items-center justify-center text-[0.65rem] font-bold ${answer === k ? 'bg-brand/20 text-brand' : 'bg-[var(--surface-2)] text-[var(--text-3)]'}`}>{k}</span>
              {v}
            </button>
          ))}
        </div>
      )}

      {q.type === 'msq' && (
        <div className="space-y-2">
          {Object.entries(q.options).map(([k, v]) => {
            const selected = Array.isArray(answer) && answer.includes(k)
            return (
              <button
                key={k}
                onClick={() => toggleMsq(k)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-xs text-left transition-all ${
                  selected
                    ? 'bg-brand/10 border-brand/40 text-brand'
                    : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)]'
                }`}
              >
                <span className={`w-5 h-5 flex-shrink-0 rounded-md flex items-center justify-center text-[0.65rem] font-bold ${selected ? 'bg-brand/20 text-brand' : 'bg-[var(--surface-2)] text-[var(--text-3)]'}`}>{k}</span>
                {v}
              </button>
            )
          })}
        </div>
      )}

      {q.type === 'true_false' && (
        <div className="flex gap-2">
          {['True', 'False'].map(opt => (
            <button
              key={opt}
              onClick={() => set(opt)}
              className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${
                answer === opt
                  ? 'bg-brand/10 border-brand/40 text-brand'
                  : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)]'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {q.type === 'short_answer' && (
        <textarea
          value={answer}
          onChange={e => set(e.target.value)}
          placeholder="Write your answer…"
          maxLength={500}
          className="w-full h-16 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 py-2 text-xs text-[var(--text-1)]
                     placeholder:text-[var(--text-3)] focus:outline-none focus:border-brand/50 resize-none transition-colors"
        />
      )}
    </div>
  )
}

export default function VirtualMode() {
  const { state, dispatch } = useExam()
  const { user } = useAuth()
  const { questions, startTime, durationSec, answers } = state
  const total    = questions.length
  const answered = Object.values(answers).filter(a => Array.isArray(a) ? a.length > 0 : a.trim().length > 0).length
  const [active,    setActive]    = useState(0)
  const [fsWarning, setFsWarning] = useState(false)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const switchRef   = useRef(0)
  const autoRef     = useRef(false)

  // Copy-paste block
  useExamGuards()

  // Fullscreen enforcement
  useEffect(() => {
    if (!EXAM_GUARDS.fullscreen) return
    document.documentElement.requestFullscreen?.().catch(() => {})

    function onFsChange() {
      if (!document.fullscreenElement) setFsWarning(true)
    }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange)
      if (document.fullscreenElement) document.exitFullscreen?.()
    }
  }, [])

  // Screen share detection
  useEffect(() => {
    if (!navigator.mediaDevices?.getDisplayMedia) return
    const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices)
    navigator.mediaDevices.getDisplayMedia = async (...args) => {
      if (!autoRef.current) {
        autoRef.current = true
        dispatch({ type: 'SET_AUTO_SUBMITTED', payload: 'screenShare' })
        dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })
      }
      return original(...args)
    }
    return () => { navigator.mediaDevices.getDisplayMedia = original }
  }, [])

  // Warn before tab close
  useEffect(() => {
    function onBeforeUnload(e) {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // Tab / window switch detection
  useEffect(() => {
    if (!EXAM_GUARDS.tabSwitch) return

    function onVisibility() {
      if (!document.hidden) return  // only count leaving, not returning
      if (autoRef.current) return   // already submitted

      autoRef.current = true
      dispatch({ type: 'SET_AUTO_SUBMITTED', payload: 'tabSwitch' })
      dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])


  const [rem, setRem] = useState(() =>
    startTime && durationSec ? Math.max(0, durationSec - (Date.now() - startTime) / 1000) : 0
  )
  const [timeUpNotice, setTimeUpNotice] = useState(false)

  useEffect(() => {
    const tick = setInterval(() => {
      if (!startTime || !durationSec) return
      const r = Math.max(0, durationSec - (Date.now() - startTime) / 1000)
      setRem(r)
      if (r <= 0 && !autoRef.current) {
        autoRef.current = true
        dispatch({ type: 'SET_AUTO_SUBMITTED' })
        setTimeUpNotice(true)
        setTimeout(() => dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING }), 2500)
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [startTime, durationSec])

  const timerPct   = durationSec ? rem / durationSec : 1
  const timerColor = rem < 60 ? '#f87171' : rem < 300 ? '#fbbf24' : '#0ea5e9'

  function scrollTo(i) {
    setActive(i)
    document.getElementById(`q-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
    {EXAM_GUARDS.watermark && user && (
      <ExamWatermark name={user.name} email={user.email} />
    )}

    {timeUpNotice && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center"
           style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(4px)' }}>
        <div className="card p-8 max-w-sm text-center space-y-3 animate-fade-up">
          <p className="text-3xl">⏰</p>
          <h3 className="text-base font-bold" style={{ color: 'var(--text-1)' }}>Time's Up!</h3>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            Your exam is being submitted automatically. Please wait…
          </p>
        </div>
      </div>
    )}

    {fsWarning && (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center"
           style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(4px)' }}>
        <div className="card p-8 max-w-sm text-center space-y-4">
          <p className="text-2xl">⚠️</p>
          <h3 className="text-base font-bold" style={{ color: 'var(--text-1)' }}>Fullscreen Required</h3>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            Exiting fullscreen is not allowed during the exam. Please return to fullscreen to continue.
          </p>
          <p className="text-sm" style={{ color: '#f87171' }}>
            ⚠ Switching to another tab or window will automatically submit your exam.
          </p>
          <button
            className="btn-primary w-full flex items-center justify-center"
            onClick={() => {
              document.documentElement.requestFullscreen?.()
              setFsWarning(false)
            }}
          >
            Return to Fullscreen
          </button>
        </div>
      </div>
    )}

<div className="max-w-7xl mx-auto px-3 sm:px-5 py-4 sm:py-6 flex gap-4 lg:gap-8">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col gap-3 w-52 flex-shrink-0 sticky top-20 self-start h-[calc(100vh-5.5rem)] overflow-hidden">
        {/* Timer */}
        <div className="card p-4 text-center flex-shrink-0">
          <div className="relative w-16 h-16 mx-auto mb-2">
            <svg viewBox="0 0 64 64" className="-rotate-90 w-full h-full">
              <circle cx="32" cy="32" r="26" fill="none" stroke="var(--surface-2)" strokeWidth="5" />
              <circle cx="32" cy="32" r="26" fill="none" strokeWidth="5" strokeLinecap="round"
                stroke={timerColor}
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - timerPct)}`}
                style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.5s' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-mono font-bold text-sm" style={{ color: timerColor }}>
              {fmt(rem)}
            </div>
          </div>
          <p className="text-[0.65rem] text-[var(--text-3)]">remaining</p>
        </div>

        {/* Question grid — scrollable */}
        <div className="card p-3 flex flex-col flex-1 min-h-0 overflow-hidden">
          <p className="text-[0.65rem] text-[var(--text-3)] font-semibold mb-2 uppercase tracking-wider flex-shrink-0">{answered}/{total} answered</p>
          <div className="grid grid-cols-5 gap-1 overflow-y-auto flex-1 content-start">
            {questions.map((_, i) => {
              const a   = answers[i]
              const ans = Array.isArray(a) ? a.length > 0 : (a ?? '').trim().length > 0
              return (
                <button
                  key={i}
                  onClick={() => scrollTo(i)}
                  className={`aspect-square rounded-md text-[0.6rem] font-semibold transition-all ${
                    i === active   ? 'bg-brand text-white'
                    : ans          ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:bg-[var(--surface)]'
                  }`}
                >
                  {i + 1}
                </button>
              )
            })}
          </div>
        </div>

      </aside>

      {/* Mobile timer bar — fixed just below navbar */}
      <div className="lg:hidden fixed top-14 inset-x-0 z-40 backdrop-blur border-b flex items-center gap-3 px-4 py-2"
           style={{ background: 'color-mix(in srgb, var(--bg) 90%, transparent)', borderColor: 'var(--border)' }}>
        <span className="font-mono font-bold text-sm flex-shrink-0" style={{ color: timerColor }}>⏱ {fmt(rem)}</span>
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${timerPct * 100}%`, background: timerColor }} />
        </div>
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>{answered}/{total}</span>
      </div>

      {/* Questions */}
      <div className="min-w-0 space-y-3 sm:space-y-4 pt-12 lg:pt-0" style={{ flex: '1 1 950px', maxWidth: '950px' }}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs sm:text-sm font-semibold text-[var(--text-2)]">⏱ {STRINGS.modes.virtual} · {total} Questions</h2>
          <p className="text-xs text-[var(--text-3)] hidden sm:block">No feedback until submission</p>
        </div>
        {questions.map((q, i) => (
          <QuestionItem key={i} q={q} idx={i} active={i === active} onClick={() => setActive(i)} />
        ))}

        {/* Submit at bottom after scrolling */}
        <div className="pt-4 pb-6 flex flex-col items-center gap-2">
          <button
            onClick={() => setShowSubmitConfirm(true)}
            className="btn-primary px-10"
          >
            Submit
          </button>
          {answered < total && (
            <p className="text-xs text-amber-400">⚠ {total - answered} question{total - answered !== 1 ? 's' : ''} unanswered</p>
          )}
        </div>
      </div>
    </div>

    {showSubmitConfirm && (
      <ConfirmModal
        title="Submit exam?"
        message={
          answered < total
            ? `You still have ${total - answered} question${total - answered !== 1 ? 's' : ''} unanswered and ${fmt(rem)} left on the clock. Once submitted, you can't come back to change answers.`
            : `You still have ${fmt(rem)} left on the clock. Once submitted, you can't come back to change answers.`
        }
        confirmLabel="Submit"
        cancelLabel="Keep working"
        danger={answered < total}
        onCancel={() => setShowSubmitConfirm(false)}
        onConfirm={() => {
          setShowSubmitConfirm(false)
          dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })
        }}
      />
    )}
    </>
  )
}
